import { channelLabel, duration, outcomeLabel, positionLabel } from "./format";
import {
  DEFAULT_INTERVAL,
  INTERVALS,
  IntervalMinutes,
  browserOffsetMinutes,
  buildGrid
} from "./timeline";
import {
  decimalMinutes,
  firstStartMs,
  lastEndMs,
  sessionsForSignUp,
  spokenSeconds,
  turnIndexFor,
  turnStamp
} from "./speakingTurns";
import type { SignUp, SpeakerParticipationReport, Timeline } from "./types";

/**
 * AB#9981. The report's tables as one CSV, built in the browser from what is on screen.
 *
 * <b>Why a second export path.</b> The existing control emails a file, which is the right
 * shape for a scheduled hand-off and the wrong shape for a clerk who is looking at the
 * numbers now and wants them in a spreadsheet before the meeting ends. This one is
 * synchronous, needs no mailbox, and carries exactly what the reader can already see --
 * so it cannot widen access to anything, including contact details, which it never writes.
 *
 * <b>No spreadsheet library.</b> A CSV needs none, and adding one to this bundle for a
 * button would cost more than the feature. The emailed path is unchanged.
 */

/** Excel reads a leading =, +, - or @ as a formula. Every one of these values is text a
 *  member of the public typed into a registration form. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

function cell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";

  let text = String(value);

  // A quote prefix is what LibreOffice and Excel both read back as literal text. It is
  // visible in the cell, which is the point: a value that looked like a formula is one the
  // reader should be able to see was neutralised.
  if (FORMULA_LEAD.test(text)) text = "'" + text;

  return '"' + text.replace(/"/g, '""') + '"';
}

function row(values: Array<string | number | null | undefined>): string {
  return values.map(cell).join(",");
}

/**
 * The interval the reader currently has applied, read off the state the control publishes.
 *
 * Resolved against the DOCUMENT, not the element this export button was rendered into. On
 * the insight-embedded surface the view bar lives in the overview insight's container and
 * the time profile lives in another one entirely, so a scoped query never matched and this
 * silently returned the default -- writing 30-minute blocks into every file no matter which
 * button the reader had pressed.
 */
function appliedInterval(host: Document | HTMLElement): IntervalMinutes {
  const doc = host instanceof Document ? host : (host.ownerDocument ?? document);
  const root = doc.querySelector<HTMLElement>('[data-testid="speaker-report-time-profile"]');
  const applied = Number(root?.getAttribute("data-interval"));
  for (const candidate of INTERVALS) {
    if (candidate === applied) return candidate;
  }
  return DEFAULT_INTERVAL;
}

/**
 * Every table on the report, in reading order, separated by a blank line and a heading.
 *
 * One file rather than one per table: a clerk attaching this to a records request attaches
 * one thing. The section headings are what makes it navigable, and they are what a reader
 * splitting it back into sheets keys on.
 */
export function buildReportCsv(
  report: SpeakerParticipationReport,
  host: Document | HTMLElement
): string {
  const lines: string[] = [];

  lines.push(row(["Speaker participation"]));
  lines.push(row(["Meeting", report.meeting.name]));
  lines.push(row(["Meeting id", report.meeting.meetingId]));
  lines.push(row(["Data read (UTC)", report.readAtUtc]));
  lines.push("");

  lines.push(row(["Does it add up"]));
  lines.push(row(["Outcome", "In person", "By phone", "Total"]));
  for (const line of report.reconciliation) {
    lines.push(row([outcomeLabel(line.outcome), line.inPerson, line.phone, line.total]));
  }
  lines.push(row(["People", report.totals.people, "Sign-ups", report.totals.signUps]));
  lines.push("");

  lines.push(row(["Agenda and who signed up"]));
  lines.push(row([
    "Section", "Item", "Title", "Signed up", "Spoke", "In person", "By phone", "Status"
  ]));
  for (const section of report.sections) {
    for (const item of section.items) {
      const spoke = item.signUps.filter((s) => s.outcome === "Spoke").length;
      const inPerson = item.signUps.filter((s) => s.channel === "InPerson").length;
      lines.push(row([
        section.title,
        item.itemNumber,
        item.title,
        item.signUps.length,
        spoke,
        inPerson,
        item.signUps.length - inPerson,
        item.statusName
      ]));
    }
  }
  lines.push("");

  // AB#9981. Start, end and the real spoken length, on the sign-up rows.
  //
  // Requested by Los Angeles County: "the individual speaker's start and stop talk times and
  // the exact amount of time spoken". Gated on `availability.timeline` for the same reason
  // the screen gates the columns -- three columns that are empty on every row of an exported
  // government record read as data somebody lost.
  //
  // ONE ordered list drives the header and every row, so the two cannot disagree about
  // position. The section used to write two literal arrays.
  const turns = turnIndexFor(report);
  const withTurnTimes = report.availability.timeline === true && turns.any;

  const signUpColumns: Array<{
    header: string;
    cell: (context: { itemNumber: string; signUp: SignUp }) => string | number | null;
  }> = [
    { header: "Item", cell: (c) => c.itemNumber },
    { header: "Speaker", cell: (c) => c.signUp.speakerName },
    { header: "Organization", cell: (c) => c.signUp.organization },
    { header: "Channel", cell: (c) => channelLabel(c.signUp.channel) },
    { header: "Outcome", cell: (c) => outcomeLabel(c.signUp.outcome) },
    { header: "Position", cell: (c) => positionLabel(c.signUp.position) },
    { header: "Speaking time", cell: (c) => duration(c.signUp.speakingSeconds) }
  ];

  if (withTurnTimes) {
    signUpColumns.push(
      {
        header: "Start time",
        // The FIRST turn and the LAST end, because a speaker may hold the microphone more
        // than once on one item and a sign-up is one row. The per-turn detail lives in the
        // speaker profile, which is the only surface whose grain can carry it.
        cell: (c) => turnStamp(firstStartMs(sessionsForSignUp(turns, c.signUp)), turns.clock)
      },
      {
        header: "End time",
        cell: (c) => turnStamp(lastEndMs(sessionsForSignUp(turns, c.signUp)), turns.clock)
      },
      {
        header: "Total time spoken (min)",
        cell: (c) => decimalMinutes(spokenSeconds(sessionsForSignUp(turns, c.signUp)).seconds)
      });
  }

  signUpColumns.push({ header: "Over cap", cell: (c) => (c.signUp.wasOverCap ? "yes" : "no") });

  lines.push(row(["Sign-ups"]));

  if (withTurnTimes) {
    // Which clock the two time columns are on. The screen says it on the heading; a file
    // attached to a records request has no heading to read.
    lines.push(row([
      "Speaking times",
      turns.clock.fromServer
        ? (turns.clock.timeZoneId || "meeting time zone")
        : "browser time zone of the person who exported this"
    ]));
  }

  lines.push(row(signUpColumns.map((column) => column.header)));
  for (const section of report.sections) {
    for (const item of section.items) {
      for (const signUp of item.signUps) {
        const context = { itemNumber: item.itemNumber, signUp };
        lines.push(row(signUpColumns.map((column) => column.cell(context))));
      }
    }
  }
  lines.push("");

  // Names and organisations only. Contact detail is NEVER written here: this file is built
  // from the payload the reader already holds, and widening it by a column would make a
  // download the way round the server's decision about who may see an email address.
  lines.push(row(["Directory"]));
  lines.push(row(["Speaker", "Organization", "Channel", "Sign-ups", "Spoke", "Total time"]));
  for (const person of report.directory) {
    lines.push(row([
      person.name,
      person.organization,
      channelLabel(person.channel),
      person.signUpCount,
      person.spokeCount,
      duration(person.totalSpeakingSeconds)
    ]));
  }
  lines.push("");

  lines.push(row(["Breakdowns"]));
  lines.push(row(["Breakdown", "Slice", "Count"]));
  const breakdowns: Array<[string, typeof report.breakdowns.position]> = [
    ["Position", report.breakdowns.position],
    ["Channel", report.breakdowns.channel],
    ["Completion", report.breakdowns.completion],
    ["Segments", report.breakdowns.segments]
  ];
  for (const [name, slices] of breakdowns) {
    for (const slice of slices) lines.push(row([name, slice.label, slice.count]));
  }
  lines.push("");

  const timeline = report.timeProfile.timeline as Timeline | undefined;
  if (report.availability.timeline === true && timeline) {
    const interval = appliedInterval(host);

    // The SAME fallback offset the screen uses. Passing 0 here meant that whenever the
    // server could not resolve the stored zone -- a Windows zone id, or a meeting with no
    // zone and a customer with none either -- the screen labelled blocks in the reader's own
    // zone and this file labelled the same blocks in UTC, hours apart, with nothing in the
    // file to say which clock it used.
    const grid = buildGrid(
      timeline, report.meeting.startUtc, interval, browserOffsetMinutes(report.meeting.startUtc));

    // The block size the reader chose, not a fixed one. A file whose rows are half-hours
    // when the screen shows quarter-hours is a file that disagrees with the report it
    // came from.
    lines.push(row(["Connections and speaking turns, " + interval + "-minute blocks"]));

    // Which clock the block column is on, said out loud. The screen says it in the heading;
    // a file handed to a records request has no heading to read.
    lines.push(row([
      "Times",
      grid.offsetFromServer
        ? (timeline.timeZoneId || "meeting time zone")
        : "browser time zone of the person who exported this"
    ]));
    lines.push(row([
      "Block", "In person", "By phone", "Turns", "Speakers", "Lines at the mark",
      "Distinct lines in the block", "Listen-only lines", "Recorded items", "Inferred items"
    ]));
    for (const bucket of grid.buckets) {
      lines.push(row([
        bucket.label,
        bucket.inPerson,
        bucket.byPhone,
        bucket.turns,
        bucket.speakers,
        bucket.atMark,
        bucket.lines,
        bucket.listenOnly,
        bucket.recorded.length,
        bucket.inferred.length
      ]));
    }

    // The two rows the screen carries under the blocks. Without them the block rows do not
    // sum to the report's own turn total, and the file says nothing about why -- which is
    // the reconciliation the report exists to guarantee.
    if (grid.outsideWindow.turns > 0) {
      lines.push(row([
        "Outside the meeting window",
        grid.outsideWindow.inPerson,
        grid.outsideWindow.byPhone,
        grid.outsideWindow.turns,
        grid.outsideWindow.speakers
      ]));
    }

    lines.push(row([
      "All day",
      grid.totalInPerson,
      grid.totalByPhone,
      grid.totalTurns,
      grid.totalSpeakers
    ]));
    lines.push("");
  }

  return lines.join("\r\n");
}

/** A filename that sorts and does not need quoting on any operating system. */
export function csvFileName(report: SpeakerParticipationReport): string {
  const stamp = (report.readAtUtc || "").slice(0, 10) || "report";
  return "speaker-participation-" + report.meeting.meetingId + "-" + stamp + ".csv";
}

/**
 * Hands the file to the browser.
 *
 * The byte-order mark is what makes Excel open a UTF-8 CSV as UTF-8; without it every
 * accented name in a Californian speaker list arrives mojibake. The object URL is revoked
 * on the next frame rather than immediately -- Safari has not started reading it yet when
 * the click handler returns.
 */
export function downloadCsv(fileName: string, text: string): void {
  const blob = new Blob(["﻿" + text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
